import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { ClaimKey } from '../../../models/api/core/claim-key';
import { ExportService } from '../../../services/export/export.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { WatchlistService } from '../../../services/view/watchlist.service';
import { ClaimExportMapper } from '../../../utilities/claim-export-mapper';
import { BaseComponent } from '../../base/base.component';
import { ExportFormat } from '../../core/buttons/export-button.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../core/panels/loading-panel.component';
import { ClaimViewComponent } from '../claim-view/claim-view.component';
import { LinkLabelComponent } from '../../core/labels/link-label.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-watchlist',
  imports: [
    CommonModule,
    RouterModule,
    ClaimViewComponent,
    EmptyStateComponent,
    LoadingPanelComponent,
    LinkLabelComponent,
  ],
  template: `
    <app-loading-panel
      [state]="state"
      loadingStyle="Skeleton"
      [loadingHeight]="450"
      [error]="error"
    >
      <ng-template #empty>
        <app-empty-state
          icon="fa-light fa-star"
          label="Empty watchlist"
          height="450px"
        >
          <span
            >Watched claims will appear here. Try
            <app-link-label
              [style.color]="'var(--sed-engaged-black-color)'"
              (click)="searchClick.emit()"
              >searching</app-link-label
            >
            for a claim.</span
          >
        </app-empty-state>
      </ng-template>
      <ng-template #loaded>
        <app-claim-view
          [claimView]="watchlist.claims()"
          [columnView]="watchlist.claims()"
          [showSelection]="true"
          [showRemove]="true"
          [showDateAccessed]="true"
          [claimViewAriaLabel]="'Watchlist'"
          [persistPaginationSettings]="true"
          [showWatchButton]="false"
          sortField="dateAccessed"
          [sortOrder]="-1"
          (removeRows)="onRemoveRows($event)"
          (export)="onExport($event)"
          (columnsUpdated)="onColumnsUpdated()"
        />
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class WatchlistComponent extends BaseComponent implements OnInit {
  private snackbar = inject(Snackbar);
  watchlist = inject(WatchlistService);
  exportService = inject(ExportService);
  private datePipe = inject(DatePipe);

  @Output()
  searchClick = new EventEmitter();

  protected state: LoadingState = 'Loading';

  error: any = null;

  constructor() {
    super();
    effect(() => {
      const claims = this.watchlist.claims();
      if (!claims) {
        this.state = 'Loading';
      } else if (claims.rows.length > 0) {
        this.state = 'Loaded';
      } else {
        this.state = 'Empty';
      }
    });
  }

  @HandleLoadingState()
  async ngOnInit() {
    if (!this.watchlist.claims()) {
      await this.watchlist.load();
    }
  }

  @HandleLoadingState()
  async onColumnsUpdated() {
    await this.watchlist.forceLoad();
  }

  @HandleLoadingState()
  async onRemoveRows(event: { type: 'selected' | 'all'; rows?: any[] }) {
    if (event.type === 'selected') {
      const rows = event.rows ?? [];
      if (rows.length === 0) {
        this.snackbar.show(
          'Please select at least one claim to remove.',
          'Error',
        );
        return;
      }

      const claimKeys: ClaimKey[] = rows.map((r) => ({
        recordId: r.recordId,
        recordSource: r.recordSource,
      }));

      this.watchlist.remove(claimKeys);
      this.snackbar.show('Selected claim(s) successfully removed', 'Success');
    } else if (event.type === 'all') {
      this.watchlist.clear();
      this.snackbar.show('Claims successfully removed', 'Success');
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onExport(format: ExportFormat) {
    const rows = this.watchlist.claims().rows;
    const columns = this.watchlist
      .claims()
      .columns.filter((col) => col.propertyName !== 'WatchStatus');
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `Watchlist_${formattedDate}.${format}`;

    if (format === 'csv') {
      const csvData = ClaimExportMapper.toCsvExport(rows, columns, 'Watchlist');
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = ClaimExportMapper.toExcelExport(
        rows,
        columns,
        'Watchlist',
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    }
  }
}
